import {
  Button,
  ClickAwayListener,
  IconButton,
  MenuItem,
  Select,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import CallIcon from 'crono-fe-common/icons/Icon-Call';
import EmailIcon from 'crono-fe-common/icons/Icon-Email';
import React, { useEffect, useMemo, useState } from 'react';
import { colors } from 'crono-fe-common/theme';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { SequenceInstanceStepWrapper } from './style';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { ConfirmEdit } from 'hooks/strategy/useOnBehalfEditConfirm';
import { PersonalizationIdentity } from 'hooks/strategy/usePersonalizationIdentity';
import { format } from 'date-fns';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { Switch } from '@crono-one/design-system';
import LinkedinInvitationIcon from 'crono-fe-common/icons/Icon-Linkedin-Invitation';
import LinkedinIconMessage from 'crono-fe-common/icons/Icon-Linkedin-Message';
import LinkedinIconInvoice from 'crono-fe-common/icons/Icon-Linkedin-Invoice';
import LinkedinIconInMailCompass from 'crono-fe-common/icons/Icon-Linkedin-Inmail-New';
import SelectedTemplateIcon from 'crono-fe-common/icons/Icon-Selected-Template';
import AddTemplateIconS from 'crono-fe-common/icons/Icon-Add-Template-S';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import {
  renderActivityIcon,
  ReplyThread,
} from 'context/strategyDetail/stepDetailComponent';
import ReplyThreadIcon from 'crono-fe-common/icons/Icon-Reply-Thread';
import EditableTemplate from 'context/strategyDetail/stepDetailComponent/editableTemplate';
import {
  stopAndJumpConditionalPresent,
  parseTaskTypeToString,
} from 'utils/businessLogic';
import ConditionalStepDetailComponent from 'context/strategyDetail/conditionalStepDetailComponent';
import { SwitchToTask } from 'crono-fe-common/types/enums/switchToConditionalStep';
import usePatchPersonalisedTemplate from 'hooks/services/personalisedTemplate/usePatchPersonalisedTemplate';
import useCreatePersonalisedTemplate from 'hooks/services/personalisedTemplate/useCreatePersonalisedTemplate';
import { useConditionalSnackBar } from 'context/snackbar';
import TimeOfDayPicker from 'components/TimeOfDayPicker';
import { useAnalytics } from 'context/analytics';
import { TemplateCardsContainerTooltip } from 'pages/accountDrawer/linkedinView/templateCardsContainerTooltip';
import DelayButton from 'context/strategyDetail/delayButton';
import { cn } from 'utils/style-utils';
import ConditionalFlowButton from 'context/strategyDetail/conditionalFlowButton';
import ThreeDotsIcon from 'crono-fe-common/icons/Icon-Three-dots';
import { OptionsWrapper } from 'pages/strategy/strategyOptions/options/style';
import PersonalisedBadge from './personalisedBadge';
import SendTestEmailOption from './SendTestEmailOption';
import { RewriteError } from 'crono-fe-common/types/enums/rewriteError';
import InformationTaskCard from '../InformationTaskCard';
import { FlexDiv } from 'crono-fe-common/components/Layout/FlexDiv';
import { RewriteWarningLabel } from '../RewriteWarningLabel';
import { GeneralWarningsLabel } from '../GeneralWarningsLabel';
import { TaskErrorsLabel } from '../TaskErrorsLabel';
import {
  getVisibleAutomationError,
  hasVisibleTaskError,
} from '../TaskErrorsLabel/taskErrorVisibility';
import { IfInvitationIsAcceptedBanner } from 'pages/strategy/createStrategyWithAI/StrategyStructureEdit/strategyStructureStep/strategyStepBanners';
import LinkedinViewProfileIcon from 'crono-fe-common/icons/Icon-Linkedin-View-Profile';
import LinkedinLikeReactionIcon from 'crono-fe-common/icons/Icon-Linkedin-Like-Reaction';
import AutoStaticBadge from 'context/strategyDetail/stepDetailComponent/AutoStaticBadge';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import CronoVoiceVisualizer from 'pages/accountDrawer/linkedinView/cronoVoiceVisualizer';
import { RewriteInProgressStatuses } from 'crono-fe-common/types/enums/rewriteStatus';
import CalendarRescheduleIcon from 'crono-fe-common/icons/Icon-Calendar-Reschedule';
import { IconTodo } from 'crono-fe-common/icons/IconTodo';
import { TodoSequenceTaskForm } from 'context/strategyDetail/stepDetailComponent/editableTemplate/TodoSequenceTaskForm';
import { dateCompare } from 'utils/dateUtils';

export interface TypeOption {
  type: TaskTodoType;
  subtype: TaskTodoSubtype | null;
  icon: JSX.Element;
  text: string;
}

export const typeOptions: TypeOption[] = [
  {
    type: TaskTodoType.CALL,
    subtype: null,
    icon: <CallIcon />,
    text: 'Call',
  },
  {
    type: TaskTodoType.EMAIL,
    subtype: null,
    icon: <EmailIcon />,
    text: 'Email',
  },
  {
    type: TaskTodoType.LINKEDIN,
    subtype: TaskTodoSubtype.LinkedinInvitation,
    text: 'Linkedin Invitation',
    icon: <LinkedinInvitationIcon />,
  },
  {
    type: TaskTodoType.LINKEDIN,
    subtype: TaskTodoSubtype.LinkedinMessage,
    text: 'Linkedin Message',
    icon: <LinkedinIconMessage />,
  },
  {
    type: TaskTodoType.LINKEDIN,
    subtype: TaskTodoSubtype.LinkedinVoiceNote,
    text: 'Linkedin Voice Note',
    icon: <LinkedinIconInvoice size={16} />,
  },
  {
    type: TaskTodoType.IN_MAIL,
    subtype: null,
    icon: <LinkedinIconInMailCompass viewBox="0 2 20 20" />,
    text: 'Linkedin InMail',
  },
  {
    type: TaskTodoType.LINKEDIN,
    subtype: TaskTodoSubtype.LinkedinProfileView,
    text: 'Linkedin Profile View',
    icon: <LinkedinViewProfileIcon />,
  },
  {
    type: TaskTodoType.LINKEDIN,
    subtype: TaskTodoSubtype.LinkedinLikeLastPost,
    text: 'Linkedin Like Reaction',
    icon: <LinkedinLikeReactionIcon />,
  },
  {
    type: TaskTodoType.GENERIC,
    subtype: null,
    icon: <IconTodo color="currentColor" />,
    text: 'To-do',
  },
];

const CustomTooltipActionTask = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip
      arrow={props.arrow ? props.arrow : false}
      {...props}
      classes={{ popper: className }}
    />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'rgba(0, 0, 0, 0.87)',
    position: 'relative',
    margin: '0px 16px 0px 0px',
    border: `1px solid ${colors.grey4}`,
    borderRadius: 16,
    padding: 0,
    WebkitBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    MozBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    OBoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    BoxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
    display: 'flex',
    width: 175,
  },
}));

interface SequenceInstanceTaskComponentProps {
  task: TaskTodo;
  handleChange: (
    type: string,
    payload: Date | null | boolean | number | string | string[],
    index: number,
  ) => void;
  index: number;
  rewriteError: RewriteError | null;
  handleJumpToStep: (index: number) => void;
  openTemplatePicker: (index: number) => void;
  handleTemplatePicker: (type: SwitchToTask) => void;
  openDateCalendar: (task: TaskTodo) => void;
  firstTask?: boolean;
  opened?: boolean;
  linkedinUntilAcceptedDays?: number;
  handleRemoveTemplate: (index: number) => void;
  tasks: TaskTodo[];
  setSequenceInstanceStepTemplateId: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  // templates tooltip
  templateComponent?: JSX.Element | null;
  closeTemplateTooltip?: () => void;
  showTemplateTab?: boolean;
  taskConditionalActivityDate?: string;
  previewOnly?: boolean;
  allowToPersonalise?: boolean;
  allowToOpenInsights?: boolean;
  onClose?: () => void;
  //Edits this task as its owner, without changing ownership. Manager only
  onBehalf?: boolean;
  //The sequence belongs to another user, so actions the API cannot perform on
  //their behalf are withheld. Distinct from onBehalf, which is only a wire flag
  sequenceNotOwned?: boolean;
  //Gates every edit; defaults to applying it straight away
  confirmEdit?: ConfirmEdit;
  //Who the template variables resolve as; defaults to the logged-in user
  personalization?: PersonalizationIdentity;
}

const SequenceInstanceTaskComponent = ({
  task,
  handleChange,
  index,
  rewriteError,
  handleJumpToStep,
  openTemplatePicker,
  handleTemplatePicker,
  openDateCalendar,
  firstTask,
  opened,
  linkedinUntilAcceptedDays,
  handleRemoveTemplate,
  tasks,
  setSequenceInstanceStepTemplateId,
  templateComponent,
  closeTemplateTooltip,
  showTemplateTab,
  taskConditionalActivityDate,
  previewOnly = false,
  allowToPersonalise,
  allowToOpenInsights,
  onClose,
  onBehalf,
  sequenceNotOwned,
  confirmEdit = (applyEdit) => applyEdit(),
  personalization,
}: SequenceInstanceTaskComponentProps) => {
  const [type, setType] = useState<TaskTodoType | null>(null);

  const tasksIncludeLinkedinWithConditional = useMemo(
    () =>
      tasks.findIndex(
        (t) => t.type === TaskTodoType.LINKEDIN && t.strategyStepConditional,
      ) !== -1,
    [tasks],
  );

  const isLinkedinInvitation =
    task.type === TaskTodoType.LINKEDIN &&
    task.subtype === TaskTodoSubtype.LinkedinInvitation;

  // These task types always let you pick a schedule time, whether or not
  // the task is automatic (unlike Invitation/LikeLastPost/ProfileView, which
  // only expose a time once automatic is turned on).
  const timeSelectorIndependentOfAutomatic =
    task.type === TaskTodoType.EMAIL ||
    task.type === TaskTodoType.CALL ||
    task.type === TaskTodoType.IN_MAIL ||
    (task.type === TaskTodoType.LINKEDIN &&
      (task.subtype === TaskTodoSubtype.LinkedinMessage ||
        task.subtype === TaskTodoSubtype.LinkedinVoiceNote));

  // Added task took the next type task, this forces to update to Email type (default)
  useEffect(() => {
    if (task.type) {
      setType(task.type);
    }
  }, [task]);

  const todaysTasks = dateCompare(new Date(task.activityDate), new Date());

  const renderStepType = (task: TaskTodo) => {
    const type = parseTaskTypeToString(task.type, task.subtype ?? null, true);

    return (
      <Typography fontSize={'12px'} fontWeight={500} paddingLeft={'12px'}>
        {type}
      </Typography>
    );
  };

  const checkPreviousSteps = (
    step: TaskTodo,
    index: number,
    steps: TaskTodo[],
  ) => {
    // first step never has previous step
    if (step.order === 1) return false;

    // Filter the previous email steps. If there are any, then there must have been previous steps in that strategy
    const previousRelatedStep = steps
      .slice(0, index)
      .filter((s) => s.type === step.type);

    return previousRelatedStep.length > 0;
  };

  const findPreviousThreadSubject = (index: number, steps: TaskTodo[]) => {
    // Only the immediate previous email step is looked to get the subject
    const previousRelatedStep = steps
      .slice(0, index)
      .filter((s) => s.type === TaskTodoType.EMAIL && !s.replyToThread)
      .pop();
    if (previousRelatedStep && previousRelatedStep.template) {
      return previousRelatedStep.template.subject;
    }
  };

  const [observerEditMode, setObserverEditMode] = useState<boolean>(false);

  const isEmail = task.type === TaskTodoType.EMAIL;
  const [isReplyThreadSubject, setIsReplyThreadSubject] = useState<ReplyThread>(
    task.replyToThread ? 'existingThread' : 'newThread',
  );
  const emailSubject = useMemo(
    () =>
      isReplyThreadSubject === 'newThread'
        ? (task.template?.subject ?? 'No subject')
        : (findPreviousThreadSubject(index, tasks) ?? 'No subject'),
    [isReplyThreadSubject, tasks, task.template, index],
  );

  const emailThreadButton = useMemo(
    () => (
      <div className="flex-center email-thread">
        {isEmail && (
          <Select
            value={isReplyThreadSubject}
            startAdornment={<ReplyThreadIcon />}
            inputProps={{ IconComponent: () => null }}
            defaultValue="newThread"
            onChange={(e) => {
              setIsReplyThreadSubject(e.target.value as ReplyThread);
              handleChange(
                'replyToThread',
                e.target.value === 'existingThread',
                index,
              );
            }}
            disabled={observerEditMode}
            sx={{
              fontSize: '14px',
              minWidth: '120px',
              boxShadow: 'none',
              fontWeight: '500',
              gap: '4px',
              padding: '0px',
              '.MuiOutlinedInput-input': {
                padding: '0px',
              },
              '.MuiSelect-select': {
                padding: '0px !important',
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover': {
                color: colors.darkGreen,
                '&& svg path': {
                  stroke: colors.darkGreen,
                },
                '&& fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
            MenuProps={{
              style: { zIndex: 10011 },
              PaperProps: {
                sx: {
                  borderRadius: '8px',
                  paddingInline: '6px',
                  border: '1px solid #dadde9',
                  boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
                  '& .MuiMenuItem-root': {
                    fontWeight: '500',
                    fontSize: '12px',
                    lineHeight: '16px',
                    height: 'fit-content',
                    padding: '8px',
                    cursor: 'pointer',
                    width: '100%',
                    borderRadius: '8px',
                    '&:hover': {
                      background: colors.primaryLight,
                      color: colors.primary,
                    },
                  },
                  '& .Mui-selected': {
                    color: colors.primary,
                    backgroundColor: 'transparent !important',
                  },
                },
              },
            }}
          >
            <MenuItem value={'newThread'}>New email thread</MenuItem>
            <MenuItem
              value={'existingThread'}
              disabled={!checkPreviousSteps(task, index, tasks)}
            >
              Previous email thread
            </MenuItem>
          </Select>
        )}
      </div>
    ),
    [tasks, task, index, isReplyThreadSubject, observerEditMode],
  );

  const {
    mutate: patchPersonalisedTemplate,
    isSuccess: personalisedTemplatePatched,
  } = usePatchPersonalisedTemplate();

  const {
    mutate: createPersonalisedTemplate,
    isSuccess: personalisedTemplateCreated,
  } = useCreatePersonalisedTemplate();

  const analytics = useAnalytics();

  const handleSaveEditableTemplate = (subject: string, content: string) => {
    if (!task.template) return;
    const isTemplatePersonalized = task.template.isPersonalized;
    //It a template is already present update it
    if (isTemplatePersonalized && task.template.personalizedTemplateId) {
      //Hoisted so the guard above still types the id inside the callback,
      //rather than a fallback silently patching template 0 if it ever loosens
      const personalizedTemplateId = task.template.personalizedTemplateId;
      confirmEdit(() => {
        patchPersonalisedTemplate({
          id: personalizedTemplateId,
          content: content,
          subject: subject,
          onBehalf,
        });
        //Inside the callback: a dismissed confirmation must not report a save
        if (analytics) {
          analytics.track('patch-personalized-template', {});
        }
      });
    } else {
      //Otherwise we create a new personalised one
      confirmEdit(() => {
        createPersonalisedTemplate({
          taskTodoId: task.id,
          content: content,
          subject: subject,
          templateId: task.template!.id,
          onBehalf,
        });
        if (analytics) {
          analytics.track('create-personalized-template', {});
        }
      });
    }
  };

  const handleSaveEditableTemplateConditional = (
    subject: string,
    content: string,
  ) => {
    if (!task.strategyStepConditional?.template) return;
    const isTemplatePersonalized =
      task.strategyStepConditional.template.isPersonalized;
    //It a template is already present update it
    if (
      isTemplatePersonalized &&
      task.strategyStepConditional.template.personalizedTemplateId
    ) {
      const personalizedTemplateId =
        task.strategyStepConditional.template.personalizedTemplateId;
      confirmEdit(() =>
        patchPersonalisedTemplate({
          id: personalizedTemplateId,
          content: content,
          subject: subject,
          onBehalf,
        }),
      );
    } else {
      //Otherwise we create a new personalised one
      const conditionalTemplateId = task.strategyStepConditional.template.id;
      confirmEdit(() =>
        createPersonalisedTemplate({
          taskTodoId: task.id,
          content: content,
          subject: subject,
          templateId: conditionalTemplateId,
          strategyStepConditionalId:
            task.strategyStepConditionalId ?? undefined,
          onBehalf,
        }),
      );
    }
  };

  useConditionalSnackBar([
    {
      condition: personalisedTemplateCreated || personalisedTemplatePatched,
      message: 'Your personalization has been saved correctly',
      severity: 'success',
    },
  ]);

  const renderTemplateManagementUI = () => (
    <div
      className="step-content-right"
      style={{
        alignSelf:
          task.strategyStepConditional && task.template
            ? 'flex-start'
            : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          overflow: 'hidden',
        }}
      >
        {task.template ? (
          <>
            {task.strategyStepTemplateId && (
              <Typography
                fontSize={14}
                fontWeight={500}
                lineHeight={'18px'}
                color={colors.grey11}
              >
                A/B testing:
              </Typography>
            )}
            <TemplateCardsContainerTooltip
              title={templateComponent}
              onClose={closeTemplateTooltip}
              open={showTemplateTab}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <div
                className={`template-section ${
                  observerEditMode ? 'disable-due-to-edit' : 'selected-template'
                }`}
                onClick={() => {
                  if (task.strategyStepTemplateId) {
                    setSequenceInstanceStepTemplateId(
                      task.strategyStepTemplateId,
                    );
                  }
                  openTemplatePicker(index);
                }}
              >
                <span className="flex-center icon-wrapper">
                  <SelectedTemplateIcon
                    className={`${
                      observerEditMode
                        ? 'step-component-button-disabled'
                        : 'step-component-button'
                    }`}
                    color={colors.grey11}
                  />
                </span>
                <Typography fontSize={14} fontWeight={500} noWrap>
                  {task.template.title}
                </Typography>
                <span
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                  }}
                  className="close-template-option"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    handleRemoveTemplate(index);
                  }}
                  id="remove-template-icon"
                >
                  <CloseMIcon color={colors.grey11} />
                </span>
              </div>
            </TemplateCardsContainerTooltip>
          </>
        ) : (
          <TemplateCardsContainerTooltip
            title={templateComponent}
            onClose={closeTemplateTooltip}
            open={showTemplateTab}
            disableFocusListener
            disableHoverListener
            disableTouchListener
          >
            <div
              className="template-section add-template"
              onClick={() => {
                openTemplatePicker(index);
              }}
            >
              <span className="flex-center icon-wrapper">
                <AddTemplateIconS className={'step-component-button'} />
              </span>
              <Typography fontSize={'14px'} fontWeight={500} noWrap>
                Add template
              </Typography>
            </div>
          </TemplateCardsContainerTooltip>
        )}
        {!!task.template && <PersonalisedBadge template={task.template} />}
      </div>
    </div>
  );

  const [openTooltipAction, setOpenTooltipAction] = useState<boolean>(false);

  const handleSkipTask = () => {
    handleChange('skip', null, index);
    setOpenTooltipAction(false);
  };

  //If the invitation is accepted and we still have some conditions aroud (due to old logic of strategies) we apply an additional margin at the top
  //of the delay buttons to make it look as the new one
  const firstTaskConditional = useMemo(() => {
    const invitationTask = tasks.find(
      (t) =>
        t.type === TaskTodoType.LINKEDIN &&
        t.subtype === TaskTodoSubtype.LinkedinInvitation,
    );
    if (invitationTask?.completed || invitationTask?.skipped) {
      return tasks.findIndex((t) => t.strategyStepConditional) === index;
    }
    return false;
  }, [tasks, index]);

  const atLeastOneErrorPresent = useMemo(() => {
    return hasVisibleTaskError(task);
  }, [task]);

  const rewriteWarningVisible =
    rewriteError && task.template && !task.completed && task.active;

  const atLeastOneWarningPresent = useMemo(() => {
    if (rewriteWarningVisible) return true;

    if (
      task.completed ||
      !task.active ||
      !task.warnings ||
      !task.warnings.length
    ) {
      return false;
    }

    return true;
  }, [task, rewriteError]);

  const stopAndJumpAlreadyPresent = useMemo(() => {
    return stopAndJumpConditionalPresent(tasks);
  }, [tasks]);

  const inRewrite = task.rewriteStatus
    ? RewriteInProgressStatuses.includes(task.rewriteStatus)
    : false;

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', position: 'relative' }}
      id={'task' + (index + 1)}
    >
      {/*This is not a component because when using :first-child to apply the style to the first one, styles to components are applied to everyone, not only to the first one */}
      <div
        style={{
          height: '100%',
          borderLeft: `1px dashed ${colors.primaryDark}`,
          position: 'absolute',
          left: 25,
          zIndex: -1,
        }}
        id="dashed-line"
      />

      <SequenceInstanceStepWrapper openedStep={opened}>
        <div className="step-row">
          {/*Jumping has no on-behalf support on the API, so it is not offered
          on a sequence owned by someone else*/}
          {!firstTask && !sequenceNotOwned && (
            <Tooltip
              title="Previous tasks will be skipped"
              PopperProps={{
                disablePortal: true,
                style: { zIndex: '999999 !important' },
              }}
              enterDelay={400}
              leaveDelay={400}
              placement="top"
              arrow
            >
              <Button
                className="jump-button"
                style={{ transform: 'translateY(50%)', bottom: '50%' }}
                onClick={() => {
                  handleJumpToStep(index);
                }}
              >
                Jump to this step
              </Button>
            </Tooltip>
          )}

          <div
            className="delay-jump-step-container"
            style={{
              justifyContent: task.strategyStepConditional
                ? 'space-around'
                : 'center',
              marginTop: firstTaskConditional ? 42 : 0,
            }}
            id={'delay' + index}
          >
            <DelayButton
              delay={task.delay ?? 0}
              decreaseDelay={() => {
                handleChange('delay', -1, index);
              }}
              increaseDelay={() => {
                handleChange('delay', 1, index);
              }}
              dateLabel={format(new Date(task.activityDate), 'MMM d, yyyy')}
              delayLabel={todaysTasks ? 'Today:' : 'Wait:'}
              tooltipTitle={
                'To change this delay please change it for the whole sequence'
              }
              disabled={true}
            />
            {task.strategyStepConditional ? (
              <DelayButton
                delay={
                  task.strategyStepConditional.daysAfterConditionNotAccepted
                }
                tooltipTitle={
                  'To change this delay please change it for the whole sequence'
                }
                disabled={true}
                increaseDelay={() => {}}
                decreaseDelay={() => {}}
                dateLabel={taskConditionalActivityDate}
              />
            ) : null}
          </div>
        </div>

        <div className="step-row">
          <div className="step-number-container">
            <Typography
              className={
                opened ? 'step-number step-number-selected' : 'step-number'
              }
            >
              {index + 1}
            </Typography>
          </div>
          <div
            className={cn({
              'step-content-conditional': task?.strategyStepConditional,
              'step-content': !task?.strategyStepConditional,
              'active-step': opened,
            })}
          >
            <div className="step-content-single-step" id={task.id.toString()}>
              <div
                className={cn('step-content-top', {
                  'step-content-no-border-bottom':
                    task?.strategyStepConditional ||
                    task?.template ||
                    atLeastOneErrorPresent ||
                    atLeastOneWarningPresent ||
                    task.type === TaskTodoType.GENERIC,
                  error: atLeastOneErrorPresent,
                  warning: !atLeastOneErrorPresent && atLeastOneWarningPresent,
                })}
                style={{
                  borderBottom: task.template ? 'none' : '',
                  height: 'fit-content',
                  ...(task.strategyStepConditional ||
                  (isLinkedinInvitation && tasksIncludeLinkedinWithConditional)
                    ? {
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'column',
                      }
                    : {}),
                }}
              >
                {task.strategyStepConditional && (
                  <IfInvitationIsAcceptedBanner
                    style={{
                      margin: 0,
                      width: '100%',
                    }}
                  />
                )}

                <div
                  style={{
                    width: '100%',
                    alignItems: 'center',
                    display: 'flex',
                  }}
                >
                  <div className="step-content-left">
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      {renderActivityIcon(task)}
                      {type && renderStepType(task)}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {task.type === TaskTodoType.LINKEDIN &&
                      (task.subtype === TaskTodoSubtype.LinkedinLikeLastPost ||
                        task.subtype ===
                          TaskTodoSubtype.LinkedinProfileView) ? (
                        <AutoStaticBadge />
                      ) : task.type === TaskTodoType.EMAIL ||
                        (task.type === TaskTodoType.LINKEDIN &&
                          task.subtype !== TaskTodoSubtype.LinkedinVoiceNote) ||
                        task.type === TaskTodoType.IN_MAIL ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            //this introduced issues on smaller screns, and behave corectly without them
                            // gap: 16,
                            // width: '203px',
                          }}
                        >
                          <Tooltip
                            arrow
                            title={
                              task.type === TaskTodoType.LINKEDIN &&
                              task.subtype ===
                                TaskTodoSubtype.LinkedinInvitation
                                ? 'Automate this step'
                                : task.templateId === null
                                  ? 'Toggle Auto. Can be turned on and off. Cannot be turned on if no template is selected'
                                  : 'Automate this step'
                            }
                            PopperProps={{
                              disablePortal: true,
                              style: { zIndex: '999999 !important' },
                            }}
                            enterDelay={400}
                            enterNextDelay={400}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Typography
                                fontSize={12}
                                fontWeight={500}
                                color={colors.grey11}
                              >
                                Auto
                              </Typography>
                              <Switch
                                checked={task.automatic}
                                disabled={
                                  !(
                                    task.type === TaskTodoType.LINKEDIN &&
                                    task.subtype ===
                                      TaskTodoSubtype.LinkedinInvitation
                                  ) && !task.template
                                }
                                onCheckedChange={(checked) => {
                                  handleChange('automatic', checked, index);
                                }}
                                className="mx-2"
                              />
                            </div>
                          </Tooltip>
                        </div>
                      ) : null}
                      {(task.automatic ||
                        timeSelectorIndependentOfAutomatic) && (
                        <TimeOfDayPicker
                          value={new Date(task.activityDate)}
                          onChange={(newValue) =>
                            handleChange('scheduleTime', newValue, index)
                          }
                          disabled={
                            !timeSelectorIndependentOfAutomatic &&
                            !(
                              task.type === TaskTodoType.LINKEDIN &&
                              (task.subtype ===
                                TaskTodoSubtype.LinkedinInvitation ||
                                task.subtype ===
                                  TaskTodoSubtype.LinkedinLikeLastPost ||
                                task.subtype ===
                                  TaskTodoSubtype.LinkedinProfileView)
                            ) &&
                            !task.template
                          }
                          className="min-w-24 max-w-24 w-24"
                          positionerClassname="z-5005"
                        />
                      )}
                    </div>
                  </div>
                  {(task.type === TaskTodoType.LINKEDIN &&
                    (task.subtype === TaskTodoSubtype.LinkedinLikeLastPost ||
                      task.subtype === TaskTodoSubtype.LinkedinProfileView)) ||
                  task.type === TaskTodoType.GENERIC ? (
                    <div
                      style={{
                        flex: 1,
                      }}
                    />
                  ) : (
                    !task.strategyStepConditional && (
                      <>
                        <div className="vertical-divider" />
                        {renderTemplateManagementUI()}
                      </>
                    )
                  )}
                  <ClickAwayListener
                    onClickAway={() => {
                      setOpenTooltipAction(false);
                    }}
                  >
                    <div className="container-sequence-instance-task">
                      <CustomTooltipActionTask
                        title={
                          <OptionsWrapper>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Button
                                className="option-button"
                                endIcon={<CloseMIcon />}
                                onClick={handleSkipTask}
                              >
                                Skip task
                              </Button>
                              <Button
                                className="option-button"
                                endIcon={<CalendarRescheduleIcon />}
                                onClick={() => {
                                  setOpenTooltipAction(false);
                                  openDateCalendar(task);
                                }}
                              >
                                Reschedule task
                              </Button>
                              {allowToPersonalise &&
                                isEmail &&
                                task.template && (
                                  <SendTestEmailOption
                                    task={task}
                                    subject={emailSubject}
                                    personalization={personalization}
                                    onClose={() => setOpenTooltipAction(false)}
                                  />
                                )}
                            </div>
                          </OptionsWrapper>
                        }
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        open={openTooltipAction}
                        onClose={() => setOpenTooltipAction(false)}
                      >
                        <div>
                          <IconButton
                            className="task-component-button-white "
                            aria-label="Step options"
                            onClick={() =>
                              setOpenTooltipAction((prev) => !prev)
                            }
                          >
                            <ThreeDotsIcon color={colors.mainDark} />
                          </IconButton>
                        </div>
                      </CustomTooltipActionTask>
                    </div>
                  </ClickAwayListener>
                </div>
              </div>

              {task?.strategyStepConditional ||
              task?.template ||
              atLeastOneErrorPresent ||
              atLeastOneWarningPresent ? (
                <div
                  className={cn('step-content-bottom', {
                    error: atLeastOneErrorPresent,
                    warning:
                      !atLeastOneErrorPresent && atLeastOneWarningPresent,
                  })}
                >
                  {(atLeastOneErrorPresent || atLeastOneWarningPresent) && (
                    <FlexDiv
                      direction={'column'}
                      padding={!task?.template ? '8px' : '8px 8px 0px 8px'}
                    >
                      {atLeastOneErrorPresent && (
                        <InformationTaskCard
                          type={'error'}
                          text={
                            <span>
                              <TaskErrorsLabel task={task} />
                              {getVisibleAutomationError(task) &&
                                task.lastFailedAttempt && (
                                  <span>
                                    {' '}
                                    Last attempt:{' '}
                                    {format(
                                      new Date(task.lastFailedAttempt),
                                      'MMM d, yyyy',
                                    )}
                                  </span>
                                )}
                            </span>
                          }
                        />
                      )}
                      {atLeastOneWarningPresent && !rewriteWarningVisible && (
                        <InformationTaskCard
                          text={
                            <span>
                              <GeneralWarningsLabel
                                task={task}
                                onRedirect={onClose}
                              />
                            </span>
                          }
                          type={'warning'}
                        />
                      )}
                      {rewriteWarningVisible && (
                        <InformationTaskCard
                          type={'warning'}
                          text={
                            <span>
                              <RewriteWarningLabel
                                rewriteFailureReason={rewriteError}
                                task={task}
                                onRedirect={onClose}
                                allowToOpenInsights={allowToOpenInsights}
                              />
                            </span>
                          }
                        />
                      )}
                    </FlexDiv>
                  )}
                  {task.strategyStepConditional && renderTemplateManagementUI()}
                  {task.template &&
                    (task.template.type === TemplateType.VOICE_NOTE ? (
                      <CronoVoiceVisualizer
                        duration={task.template.recordingDuration}
                        templateId={task.template.id}
                        allowToRemove={false}
                        size={task.strategyStepConditional ? 'small' : 'medium'}
                      />
                    ) : (
                      <EditableTemplate
                        id={
                          task.strategyStepConditional
                            ? 'editable-template-conditional-left-leaf'
                            : undefined
                        }
                        applyIdOnlyOnEditMode={true}
                        allowToPersonalize={allowToPersonalise}
                        previewOnly={previewOnly}
                        key={`${task.template.id}-${task.template.isPersonalized}-${task.template.personalizedTemplateId ?? 0}-${emailSubject}`}
                        emailThreadButton={
                          isEmail ? emailThreadButton : undefined
                        }
                        subject={isEmail ? emailSubject : task.template.subject}
                        enableSubjectMutation={
                          isReplyThreadSubject === 'newThread'
                        }
                        stepId={task.id}
                        template={task.template}
                        setObserverEditMode={(value) =>
                          setObserverEditMode(value)
                        }
                        onSaveReturnTemplate={handleSaveEditableTemplate}
                        prospectId={task.prospectId}
                        accountId={task.accountId}
                        isSequenceInstance={true}
                        personalization={personalization}
                        isRewriting={inRewrite}
                        onCcChange={(emails) =>
                          handleChange('ccAddresses', emails, index)
                        }
                        ccAddresses={task.ccAddresses}
                      />
                    ))}
                </div>
              ) : task.type === TaskTodoType.GENERIC ? (
                <div className="step-content-bottom">
                  <TodoSequenceTaskForm
                    key={task.id}
                    step={task}
                    onSubjectChange={(subject) =>
                      handleChange('subject', subject, index)
                    }
                    onDescriptionChange={(description) =>
                      handleChange('description', description, index)
                    }
                  />
                </div>
              ) : null}
            </div>
            {/* Conditional step */}
            {task?.strategyStepConditional ? (
              <div className="conditional-step-wrapper">
                <ConditionalStepDetailComponent
                  allowToPersonalize={allowToPersonalise}
                  conditionalStep={task.strategyStepConditional}
                  handleChange={null}
                  index={index}
                  openConditionalTemplatePicker={handleTemplatePicker}
                  previousThreadSubject={findPreviousThreadSubject(
                    index,
                    tasks,
                  )}
                  onSaveReturnTemplate={handleSaveEditableTemplateConditional}
                  onSuccessEditTemplate={() => {}}
                  disableEditsButTemplate={true}
                  prospectId={task.prospectId}
                  accountId={task.accountId}
                  isSequenceInstance={true}
                  personalization={personalization}
                  previewOnly={previewOnly}
                  stopAndJumpAlreadyPresent={stopAndJumpAlreadyPresent}
                />
              </div>
            ) : null}
          </div>
        </div>

        {isLinkedinInvitation && tasksIncludeLinkedinWithConditional && (
          <div className="step-row">
            <div
              className="step-content"
              style={{
                marginLeft: 'auto',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ConditionalFlowButton
                id={`${task.id}-conditional-button`}
                delay={linkedinUntilAcceptedDays ?? 0}
                disabledText={
                  'To change this conditional settings change it for the whole sequence'
                }
              />
            </div>
          </div>
        )}
      </SequenceInstanceStepWrapper>
    </div>
  );
};

export default SequenceInstanceTaskComponent;
